/**
 * Centralized frontend cache service
 * Stores data in memory to prevent UI flashing during navigation/reloads
 */

class FrontendCache {
  private readonly cache: Map<string, any>
  constructor() {
    this.cache = new Map()
  }

  /**
   * Get a value from cache
   * @param {string} key - Cache key
   * @returns {any} Cached value or undefined
   */
  get(key: string): any {
    return this.cache.get(key)
  }

  /**
   * Set a value in cache
   * @param {string} key - Cache key
   * @param {any} value - Value to cache
   */
  set(key: string, value: any): void {
    this.cache.set(key, value)
  }

  /**
   * Check if a key exists in cache
   * @param {string} key - Cache key
   * @returns {boolean}
   */
  has(key: string): boolean {
    return this.cache.has(key)
  }

  /**
   * Remove a value from cache
   * @param {string} key - Cache key
   */
  delete(key: string): void {
    this.cache.delete(key)
  }

  /**
   * Clear all cached values
   */
  clear(): void {
    this.cache.clear()
  }

  /**
   * Get or set a value with a factory function
   * If the key exists, returns the cached value
   * Otherwise, calls the factory function, caches the result, and returns it
   * @param {string} key - Cache key
   * @param {Function} factory - Function to generate value if not cached
   * @returns {any} Cached or newly generated value
   */
  async getOrSet(key: string, factory: () => Promise<any>): Promise<any> {
    if (this.has(key)) {
      return this.get(key)
    }
    const value = await factory()
    this.set(key, value)
    return value
  }

  /**
   * Update a cached value and return the new value
   * @param {string} key - Cache key
   * @param {Function} updater - Async function to fetch new value
   * @returns {any} New value
   */
  async update(key: string, updater: () => Promise<any>): Promise<any> {
    const value = await updater()
    this.set(key, value)
    return value
  }
}

// Export singleton instance
export const cache = new FrontendCache()

// Cache keys - centralized to avoid typos
export const CACHE_KEYS = {
  USER_ME: 'user:me',
  ACTION_COUNTS: 'action:counts',
  THEME_PREFERENCE: 'theme:preference',
  IMPERSONATION_STATE: 'impersonation:state',
  ALL_TAGS: 'tags:all',
}
